import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { autoLinkText } from "@/lib/auto-link";
import { SafeImage } from "@/components/site/SafeImage";
import { COUNTRIES, getCountry, flagUrl, roundFlagUrl, RECOMMENDED, type CountrySlug } from "@/data/countries";
import { ArticleSidebar } from "@/components/site/Sidebar";
import { KeyTakeaways } from "@/components/site/KeyTakeaways";

import { ShareBar } from "@/components/site/ShareBar";
import { articlesForCountry } from "@/lib/article-index.generated";
import { ArrowRight, Clock } from "lucide-react";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { makeSeoDescription } from "@/lib/seo-description";
import { countryImage } from "@/lib/country-image";
import { CountryTopicNav } from "@/components/site/KeywordNavBlock";
import { toAbsoluteUrl } from "@/lib/social";


const VALID = new Set(COUNTRIES.map((c) => c.slug));

export const Route = createFileRoute("/$country")({
  beforeLoad: ({ params }) => {
    if (!VALID.has(params.country as CountrySlug)) throw notFound();
  },
  head: ({ params }) => {
    const c = getCountry(params.country);
    if (!c) return {};
    const title = `${c.name} Migration Guide 2026 | LifeWestWay`;
    const description = c.heroBlurb;
    const url = `https://lifewestway.com/${c.slug}`;
    // Social previews stay on PNG — some scrapers still don't render WebP.
    const image = toAbsoluteUrl(
      countryImage(c.slug) ?? `https://flagcdn.com/w1280/${c.code}.png`,
    );

    const imageAlt = `${c.name} landmark — ${c.name} migration, jobs and cost of living guide 2026`;
    return {
      meta: [
        { title },
        { name: "description", content: makeSeoDescription(description) },
        { property: "og:title", content: title },
        { property: "og:description", content: makeSeoDescription(description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: imageAlt },
        { property: "og:site_name", content: "LifeWestWay" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: makeSeoDescription(description) },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: imageAlt },
      ],
      links: [
        { rel: "canonical", href: url },
        {
          rel: "preload",
          as: "image",
          href: flagUrl(c.code, 1280),
          imageSrcSet: `${flagUrl(c.code, 640)} 640w, ${flagUrl(c.code, 1280)} 1280w`,
          imageSizes: "100vw",
          fetchPriority: "high",
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-foreground">Country not found</h1>
      <p className="mt-2 text-muted-foreground">Try one of our six covered destinations.</p>
      <div className="mt-6">
        <Link title="Return to the LifeWestWay homepage" to="/" className="text-accent underline">Back to home</Link>
      </div>
    </div>
  ),
  component: CountryPage,
});

function CountryPage() {
  const { country } = Route.useParams();
  const c = getCountry(country)!;
  const articles = RECOMMENDED[c.slug];

  return (
    <>
      {/* Hero banner */}
      <section className="relative isolate overflow-hidden bg-navy text-navy-foreground">
        <SafeImage width={1280} height={720}
          eager
          src={flagUrl(c.code, 1280)}
          srcSet={`${flagUrl(c.code, 640)} 640w, ${flagUrl(c.code, 1280)} 1280w`}
          sizes="100vw"
          alt={`${c.name} flag background`}
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25"
        />

        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />

        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="flex items-center gap-3">
            <SafeImage
              src={roundFlagUrl(c.code)}
              alt={`${c.name} flag`}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full ring-2 ring-accent"
            />
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-accent">
              {c.tagline}
            </p>
          </div>

          <h1 className="mt-5 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            {c.name} Migration, Jobs, Tax & Lifestyle Guide
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/80 sm:text-lg">
            This {c.name} guide covers migration and visa routes, the {c.name} job market,
            tax rules and everyday lifestyle costs in one place. {c.heroBlurb}
          </p>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        

        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <article className="prose-article lg:col-span-8">
            <p className="text-lg leading-relaxed text-foreground">
              Moving to {c.name} is one of the biggest decisions you'll make. This guide brings together immigration pathways, real job market conditions, tax obligations, and the on-the-ground lifestyle intel you need — all in one clean, updated place.
            </p>

            <KeyTakeaways
              explicit={[
                `Visas and immigration: this hub maps the main ${c.name} routes — work, study, family and investor — with eligibility, current fees and realistic processing times.`,
                `Jobs: demand is strongest in technology, healthcare, skilled trades and finance, and we flag which employers actually sponsor foreign candidates.`,
                `Tax and money: you get plain-English coverage of residency rules, headline rates, double-taxation relief and how to open a local bank account as a newcomer.`,
                `Cost of living: city-by-city rent, transport, groceries and healthcare figures so you can budget before you commit to a move.`,
                `Everything is sourced from official ${c.name} government bodies and dated, so you can check the figure that matters before you file or pay.`,
              ]}
            />

            <h2>Current Job Market</h2>
            <p>
              {autoLinkText(`${c.name}'s employment landscape rewards specialists. Tech, healthcare, skilled trades, and finance continue to lead demand, while remote-first roles have opened doors for talent that would once have needed sponsorship. We track hiring signals across the country's top employers and regional hubs so you can time your move.`, `/${c.slug}`)}
            </p>
            <h3>Part-time Roles for Students</h3>
            <p>
              {autoLinkText(`International students in ${c.name} typically balance study with part-time work in retail, hospitality, tutoring, on-campus roles, and increasingly, remote freelance work. Our verified listings filter for genuinely student-friendly employers.`, `/${c.slug}`)}
            </p>
            <p>
              {autoLinkText(`We check each role against local labor rules, expected working hours, and pay ranges so you can plan realistic monthly income against your rent, groceries, and study costs.`, `/${c.slug}`)}
            </p>


            <h3>High-Demand Sectors</h3>
            <p>
              Beyond entry-level, {c.name}'s in-demand sectors reward mid-career professionals with the right credentials. We break down which roles trigger fast-track visa nominations and which employers regularly sponsor foreign talent.
            </p>

            <h2>Immigration Pathways</h2>
            <p>
              {autoLinkText(`There is no single "best" visa — the right pathway depends on age, occupation, family situation, education, and how quickly you want permanent residency. We map each option side-by-side with realistic timelines and cost estimates.`, `/${c.slug}`)}
            </p>
            <h3>Skilled Worker Routes</h3>
            <p>
              {autoLinkText(`Skilled routes typically weigh occupation shortage lists, English proficiency, work experience, and formal qualifications. Small preparation choices — the right IELTS band, a well-drafted employer reference — can dramatically shorten your timeline.`, `/${c.slug}`)}
            </p>
            <h3>Study-to-Work Routes</h3>
            <p>
              {autoLinkText(`Many expats enter ${c.name} on a student visa, then transition to a post-study work visa. We cover which courses and universities offer the smoothest transition into skilled employment.`, `/${c.slug}`)}
            </p>

            

            <h2>Tax & Personal Finance</h2>
            <p>
              {autoLinkText(`Understanding your tax residency status in ${c.name} is essential before your first paycheque lands. Small missteps in your first year can cost thousands in over-withholding or missed reliefs.`, `/${c.slug}`)}
            </p>
            <h4>Tax Obligations</h4>
            <p>
              {autoLinkText(`We cover filing deadlines, brackets, and the deductions most commonly missed by new arrivals — including relocation costs, home office, and cross-border pension contributions.`, `/${c.slug}`)}
            </p>
            <h4>Banking, Credit, and Long-Term Wealth</h4>
            <p>
              {autoLinkText(`Opening the right accounts, building local credit, and structuring investments for a country you may or may not stay in long-term deserves a clear head. Our finance briefs are written for people, not spreadsheets.`, `/${c.slug}`)}
            </p>

            <h2>Lifestyle & Settling In</h2>
            <p>
              {autoLinkText(`The best move plans include the human stuff: healthcare enrollment, driver's licence conversion, phone plans, and how to find community in a new city. We keep this section updated with practical, dated checklists.`, `/${c.slug}`)}
            </p>
          </article>

          <div className="lg:col-span-4">
            <ArticleSidebar activeCountry={c.slug} />
          </div>
        </div>

        {/* Keyword-matched topic navigation — auto-populated as new guides publish */}
        <CountryTopicNav country={c.slug} countryLabel={c.name} />


        {/* Featured full articles (Canada only for now) */}
        {c.slug === "canada" && articlesForCountry("canada").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured Canada Articles
              </h2>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
                In-depth guides
              </span>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("canada").map((a) => (
                <Link title={`Read: ${a.title} (Canada guide)`}
                  key={a.slug}
                  to="/canada/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — Canada guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured full articles (USA) */}
        {c.slug === "usa" && articlesForCountry("usa").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured USA Articles
              </h2>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
                In-depth guides
              </span>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("usa").map((a) => (
                <Link title={`Read: ${a.title} (USA guide)`}
                  key={a.slug}
                  to="/usa/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — USA guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured full articles (UK) */}
        {c.slug === "uk" && articlesForCountry("uk").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured UK Articles
              </h2>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
                In-depth guides
              </span>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("uk").map((a) => (
                <Link title={`Read: ${a.title} (UK guide)`}
                  key={a.slug}
                  to="/uk/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — UK guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured full articles (Australia) */}
        {c.slug === "australia" && articlesForCountry("australia").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured Australia Articles
              </h2>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
                In-depth guides
              </span>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("australia").map((a) => (
                <Link title={`Read: ${a.title} (Australia guide)`}
                  key={a.slug}
                  to="/australia/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — Australia guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured full articles (Italy) */}
        {c.slug === "italy" && articlesForCountry("italy").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex items-end justify-between">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured Italy Articles
              </h2>
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
                In-depth guides
              </span>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("italy").map((a) => (
                <Link title={`Read: ${a.title} (Italy guide)`}
                  key={a.slug}
                  to="/italy/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — Italy guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Featured full articles (Portugal) */}
        {c.slug === "portugal" && articlesForCountry("portugal").length > 0 && (
          <section className="mt-16 border-t border-border pt-12">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                Featured Portugal Articles
              </h2>
              <Link title="Browse the full library of Portugal articles on LifeWestWay"
                to="/portugal/articles"
                className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline"
              >
                View all Portugal articles <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {articlesForCountry("portugal").map((a) => (
                <Link title={`Read: ${a.title} (Portugal guide)`}
                  key={a.slug}
                  to="/portugal/$slug"
                  params={{ slug: a.slug }}
                  className="group luxe-tile flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <SafeImage width={1200} height={675}
                      src={a.heroImage}
                      alt={`${a.title} — Portugal guide`}
                      loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent backdrop-blur">
                      {a.category}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="line-clamp-2 font-display text-lg font-bold text-foreground group-hover:text-accent">
                      {a.title}
                    </h3>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {a.description}
                    </p>
                    <div className="mt-auto pt-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-accent">
                        Read guide <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}




        {/* Recommended reads inline */}
        <section className="mt-16 border-t border-border pt-12">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
              More from {c.shortName}
            </h2>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <article
                key={a.slug}
                className="group rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-md"
              >
                <div className="flex items-center gap-2">
                  <SafeImage width={64} height={64}
                    src={roundFlagUrl(c.code)}
                    alt={`${c.name} flag`}
                    className="h-6 w-6 rounded-full ring-1 ring-border"
                    aria-hidden
                  />
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-accent">
                    {a.category}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-lg font-bold text-foreground group-hover:text-accent">
                  {a.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  A concise, human-edited briefing with checklists, links, and next steps.
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* Editorial standards link */}
        <div className="mt-12 flex flex-col items-start gap-3 rounded-2xl border border-accent/25 bg-card/60 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-base font-bold text-foreground">
              How we keep {c.name} guides accurate
            </h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              We check official government sources before we publish and update every guide
              when rules change. Read our editorial standards.
            </p>
          </div>
          <Link title="Read the LifeWestWay mission and editorial standards"
            to="/mission-vision"
            className="inline-flex shrink-0 items-center rounded-md bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
          >
            Our Mission & Vision →
          </Link>
        </div>

        <ArticleDisclaimer />
      </div>
      <ShareBar title={`${c.name} Migration, Jobs, Tax & Lifestyle Guide`} />
    </>
  );
}
